import { translate } from "app/filters/i18nFilter";
import { isEmpty, some } from "lodash";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import type { Topic } from "app/pages/sales-intelligence/sub-modules/intent/types/common";
import SignalIntentTopicsFilter from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalIntentTopicsFilter/SignalIntentTopicsFilter";
import { StyledModalFooter } from "app/pages/sales-intelligence/sub-modules/signals/components/modals/SignalsModal/styles";
import SignalBasicLabel from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalBasicLabel/SignalBasicLabel";
import { StyledDropdownLabelWrapped } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalsDropdownLabel/styles";
import {
    StyledFiltersViewContent,
    StyledFiltersViewInner,
    StyledNotificationWrapper,
} from "app/pages/sales-intelligence/sub-modules/signals/components/styles";
import useSignalFiltersState from "app/pages/sales-intelligence/sub-modules/signals/hooks/useFiltersState";
import type IntentSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/intent/IntentSignalFilters";
import { FilterType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import type SignalFiltersViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersViewProps";
import type { FC } from "react";
import type { ICountryObject } from "app/services/CountryService";
import SignalMultiselectCountriesFilterTracked from "../../../containers/SignalMultiselectCountriesFilter/SignalMultiselectCountriesFilterTracked";
import { StyledFlexRowWrap, StyledIntentInlineFilterWrapper } from "./styles";
import { initialiseFiltersState } from "./utils";

type IntentSignalFiltersViewProps = SignalFiltersViewProps<IntentSignalFilters> & {
    topics: readonly Topic[];
    countries: readonly ICountryObject[];
};

const IntentSignalFilterView: FC<IntentSignalFiltersViewProps> = ({
    signalAlias,
    filters,
    topics,
    countries,
    renderSubmit,
    notificationView,
    onFiltersChange,
    onFiltersSubmit,
}) => {
    const { currentFilters, setCurrentFilters } = useSignalFiltersState(
        initialiseFiltersState({ filters, countries }),
        onFiltersChange,
    );

    const handleFilterChange = (updatedFilters: Partial<IntentSignalFilters>): void => {
        setCurrentFilters((prevFilters) => ({ ...prevFilters, ...updatedFilters }));
    };

    const handleTopicsChange = (topics: readonly string[]): void => {
        handleFilterChange({ topics });
    };

    const handleCountryChange = (countries: readonly number[]): void => {
        handleFilterChange({ countries });
    };

    const handleSubmit = () => {
        onFiltersSubmit(currentFilters);
    };

    return (
        <StyledFiltersViewInner alignItems="center">
            <StyledFiltersViewContent>
                <SignalBasicLabel>{translate("si.signals.intent.label.companies")}</SignalBasicLabel>

                <StyledFlexRowWrap
                    alignItems="center"
                    gap="8px"
                >
                    <StyledIntentInlineFilterWrapper>
                        <SignalIntentTopicsFilter
                            filterKey={FilterType.IntentTopics}
                            selectedValue={currentFilters.topics}
                            autoOpen={!currentFilters?.topics || currentFilters?.topics?.length === 0}
                            options={topics}
                            onValueChange={handleTopicsChange}
                        />
                    </StyledIntentInlineFilterWrapper>

                    <I18n
                        element={StyledDropdownLabelWrapped}
                        translatable={{
                            key: `si.signals.intent.employees-from.prefix`,
                        }}
                    />

                    <SignalMultiselectCountriesFilterTracked
                        filterKey={`${FilterType.Countries}-intent`}
                        signalAlias={signalAlias}
                        selectedValue={currentFilters.countries}
                        options={countries}
                        withAllOptionsCount={false}
                        onValueChange={handleCountryChange}
                    />
                </StyledFlexRowWrap>

                {notificationView ? <StyledNotificationWrapper>{notificationView}</StyledNotificationWrapper> : null}
            </StyledFiltersViewContent>

            <StyledModalFooter>
                {renderSubmit({
                    isDisabled: some(currentFilters, isEmpty),
                    onSubmit: handleSubmit,
                })}
            </StyledModalFooter>
        </StyledFiltersViewInner>
    );
};

export default IntentSignalFilterView;
